import React from 'react';
import * as Lucide from 'lucide-react';
import { CheckCircle2, MinusCircle, XCircle, AlertCircle } from 'lucide-react';
import { COMPARISON, WHY_HERTZ, QUALITY_ASSURANCE, RUGGED_BADGES, APPLICATIONS } from '../mock';

const LightIcon = ({ name, size = 40, iconSize = 20 }) => {
  const Icon = Lucide[name] || Lucide.Circle;
  return (
    <div
      className="inline-flex items-center justify-center rounded-full bg-[#FFF3E4] text-[#F5901F] shrink-0"
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <Icon size={iconSize} strokeWidth={2.2} />
    </div>
  );
};

const StatusIcon = ({ status }) => {
  const map = {
    yes: <CheckCircle2 className="text-emerald-500" size={18} />,
    equal: <MinusCircle className="text-amber-500" size={18} />,
    limited: <AlertCircle className="text-amber-500" size={18} />,
    no: <XCircle className="text-red-500" size={18} />,
  };
  return map[status] || map.equal;
};

const ComparisonSection = () => {
  // Group advantages by operational themes
  const advantageGroups = [
    {
      number: '01',
      title: 'Communication Efficiency',
      items: [
        { icon: 'AudioLines', title: 'FULL DUPLEX COMMUNICATION', desc: 'Simultaneous talk and listen for natural conversations and efficient teamwork.' }
      ]
    },
    {
      number: '02',
      title: 'Hands-Free Mission Operation',
      items: [
        { icon: 'Hand', title: 'HANDS-FREE OPERATION', desc: '100% hands free communication allows complete focus on mission critical tasks.' },
        { icon: 'Users', title: 'REAL-TIME TEAM CONNECT', desc: 'Instant, clear communication with all team members.' }
      ]
    },
    {
      number: '03',
      title: 'Mobility & Deployment',
      items: [
        { icon: 'Feather', title: 'ULTRA LIGHTWEIGHT', desc: 'All models < 120 grams (including battery) for maximum comfort and mobility.' },
        { icon: 'HardHat', title: 'HELMET & UNIFORM MOUNTABLE', desc: 'Secure and ergonomic mounting for helmets, uniforms and gear.' },
        { icon: 'Timer', title: 'INSTANT DEPLOYMENT', desc: 'Pocket sized, mission ready in seconds.' }
      ]
    },
    {
      number: '04',
      title: 'Extended Communication Coverage',
      items: [
        { icon: 'Radio', title: 'PORTABLE BOOSTER COMPATIBLE', desc: 'Extends the existing communication network by up to 1–2 KM (LOS) with the portable booster.' }
      ]
    },
    {
      number: '05',
      title: 'Mission Reliability',
      items: [
        { icon: 'ShieldCheck', title: 'RUGGED & RELIABLE', desc: 'IP65+ protection, built for extreme environments.' },
        { icon: 'Layers', title: 'ONE SYSTEM. MULTIPLE MISSIONS.', desc: 'HERTZX adapts to any scenario, delivering seamless, reliable communication when it matters most.' }
      ]
    }
  ];

  return (
    <section id="compare" className="light-section relative py-16 md:py-24">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
        {/* Header */}
        <div className="reveal-on-scroll">
          <h2 className="font-display font-black text-3xl md:text-5xl leading-tight text-navy mt-2">
            HERTZX&trade; INTEGRATED COMMUNICATION SYSTEM
            <span className="block mt-2">VS <span className="orange-accent">CONVENTIONAL HANDHELD RADIO</span></span>
          </h2>
          <div className="text-navy-70 text-sm mt-3 tracking-wide">Mission-Critical Communication Technology Evaluation</div>
          <div className="mt-4 h-1 w-16 rounded orange-bar" />
        </div>

        {/* Executive Comparison Matrix */}
        <div className="mt-10 light-card rounded-xl overflow-hidden reveal-on-scroll">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left">
                  <th className="px-5 py-4 font-bold bg-[#0A1628] text-white border-r border-white/10">CAPABILITY</th>
                  <th className="px-5 py-4 font-black bg-[#F5901F] text-black border-r border-black/5">
                    HERTZX&trade; SYSTEM
                    <div className="text-xs font-medium mt-0.5">Full Duplex Communication</div>
                  </th>
                  <th className="px-5 py-4 font-bold bg-[#0A1628] text-white">
                    CONVENTIONAL HANDHELD RADIO
                    <div className="text-xs text-white/70 font-normal mt-0.5">Push-to-Talk / Half Duplex</div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E6EAF0]">
                {[
                  { capability: 'Communication', hertz: 'Full Duplex', conv: 'Push-to-Talk / Half Duplex' },
                  { capability: 'Operation', hertz: 'Hands-Free / Voice Activated', conv: 'Push-to-Talk' },
                  { capability: 'Mounting', hertz: 'Helmet / Uniform', conv: 'Primarily Handheld' },
                  { capability: 'Weight', hertz: 'All Models < 120 g', conv: 'Generally Heavier (varies by model)' },
                  { capability: 'Deployment', hertz: 'Seconds – Pocket sized, mission ready', conv: 'Relatively slower to deploy' },
                  { capability: 'Booster', hertz: 'Portable Booster extends network by up to 1–2 KM (LOS)', conv: 'Booster / Repeater options available in some professional series' },
                ].map((row, idx) => (
                  <tr key={idx} className="hover:bg-[#FFF7EC] transition-colors">
                    <td className="px-5 py-3 font-semibold text-navy align-top border-r border-[#E6EAF0]">{row.capability}</td>
                    <td className="px-5 py-3 text-navy align-top bg-[#FFF3E4]/40 font-medium border-r border-[#E6EAF0]">{row.hertz}</td>
                    <td className="px-5 py-3 text-navy-70 align-top">{row.conv}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Operational Advantages - Grouped by theme */}
        <div className="mt-16 reveal-on-scroll">
          <h3 className="font-display font-black text-2xl md:text-3xl text-navy mb-8">
            OPERATIONAL ADVANTAGES OF <span className="orange-accent">HERTZX&trade;</span>
          </h3>
          <div className="space-y-8">
            {advantageGroups.map((group) => (
              <div key={group.number} className="light-card rounded-xl p-6 md:p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="text-4xl md:text-5xl font-black text-[#F5901F]/20">{group.number}</div>
                  <h4 className="text-xl md:text-2xl font-bold text-navy mt-2">{group.title}</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 ml-0 md:ml-16">
                  {group.items.map((item) => (
                    <div key={item.title} className="flex items-start gap-3">
                      <LightIcon name={item.icon} size={40} iconSize={20} />
                      <div>
                        <div className="text-[#F5901F] font-bold text-sm">{item.title}</div>
                        <div className="text-navy-70 text-sm mt-1">{item.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ComparisonSection;